import { useEffect, useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth.store';
import { AppShell } from './components/layout/AppShell';
import { useDatabase } from './db/DatabaseProvider';
import { UpdatePrompt } from './components/layout/UpdatePrompt';
import { supabase } from './lib/supabase';
import { RequirePermission } from './components/auth/RequirePermission';
import { PageSkeleton } from './components/common/PageSkeleton';

// Synchronous core entry pages to prevent top-level Suspense unmounts on setup/login
import { Login } from './pages/Login';
import { SetupWizard } from './pages/SetupWizard';

import { Dashboard } from './pages/Dashboard';
import { Calendar } from './pages/Calendar';
import { Sales } from './pages/Sales';
import { ExchangeRates } from './pages/ExchangeRates';
import { Customers } from './pages/Customers';
import { Staff } from './pages/Staff';
import { Settings } from './pages/Settings';
import { Users } from './pages/Users';
import { Catalog } from './pages/Catalog';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const db = useDatabase();

  const [waited, setWaited] = useState(false);
  const [isInitialized, setIsInitialized] = useState<boolean | null>(null);
  const [checkingInit, setCheckingInit] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setWaited(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setCheckingInit(false);
      return;
    }

    setCheckingInit(true);
    let active = true;

    const runInitCheck = async () => {
      try {
        // 1. Check local IndexedDB RxDB for system_initialized flag
        const localDoc = await db.app_config.findOne({ selector: { key: 'system_initialized' } }).exec();
        if (active && localDoc && localDoc.value === 'true') {
          setIsInitialized(true);
          setCheckingInit(false);
          return;
        }

        // 2. Check if local database already has active branches (meaning system was already configured)
        const localBranch = await db.branches.findOne({ selector: { is_deleted: false } }).exec();
        if (active && localBranch) {
          try {
            await db.app_config.upsert({
              id: 'config-sistema-inicializado',
              key: 'system_initialized',
              value: 'true',
              owner_only: false,
              updated_at: new Date().toISOString(),
              is_deleted: false,
              last_modified_by: 'system_auto_heal',
            });
          } catch (e) {}
          setIsInitialized(true);
          setCheckingInit(false);
          return;
        }
      } catch (err) {
        console.warn('[App] Error reading local config or branches:', err);
      }

      if (!active) return;

      // 3. Fallback to Supabase REST if not yet initialized locally
      if (supabase) {
        try {
          // Check if system_initialized exists in cloud
          const { data: configData } = await supabase
            .from('app_config')
            .select('value')
            .eq('key', 'system_initialized')
            .eq('is_deleted', false)
            .maybeSingle();

          if (!active) return;

          if (configData && configData.value === 'true') {
            setIsInitialized(true);
            setCheckingInit(false);
            return;
          }

          // Check if cloud already has branches (system was already seeded/configured)
          const { data: branchData } = await supabase
            .from('branches')
            .select('id')
            .eq('is_deleted', false)
            .limit(1);

          if (!active) return;

          if (branchData && branchData.length > 0) {
            // Auto-heal system_initialized in cloud and local DB
            try {
              await supabase.from('app_config').upsert({
                id: 'config-sistema-inicializado',
                key: 'system_initialized',
                value: 'true',
                owner_only: false,
                updated_at: new Date().toISOString(),
                is_deleted: false,
                last_modified_by: 'system_auto_heal',
              });
              await db.app_config.upsert({
                id: 'config-sistema-inicializado',
                key: 'system_initialized',
                value: 'true',
                owner_only: false,
                updated_at: new Date().toISOString(),
                is_deleted: false,
                last_modified_by: 'system_auto_heal',
              });
            } catch (e) {
              console.warn('[App] Error auto-healing system_initialized flag:', e);
            }
            setIsInitialized(true);
            setCheckingInit(false);
            return;
          }

          setIsInitialized(false);
          setCheckingInit(false);
        } catch (err) {
          console.warn('[App] Server init check failed, falling back to local DB:', err);
          if (active) {
            setIsInitialized(false);
            setCheckingInit(false);
          }
        }
      } else {
        setIsInitialized(false);
        setCheckingInit(false);
      }
    };

    runInitCheck();

    return () => {
      active = false;
    };
  }, [db, isAuthenticated]);

  if ((isLoading || checkingInit) && !waited) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-secondary">
        <div className="flex flex-col items-center gap-3">
          <svg
            className="h-10 w-10 animate-spin text-brand-orange"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
          <p className="text-sm font-medium text-brand-deep dark:text-brand-sky">
            Verificando sesión…
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isInitialized === false) {
    return <SetupWizard />;
  }

  return <>{children}</>;
}

export function App() {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppShell>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <RequirePermission permission="reports:view" fallback={<Navigate to="/customers" replace />}>
                        <Dashboard />
                      </RequirePermission>
                    }
                  />
                  <Route path="/agenda" element={<Calendar />} />
                  <Route path="/ventas" element={<Sales />} />
                  <Route
                    path="/customers"
                    element={
                      <RequirePermission permission="clients:read" fallback={<Navigate to="/" replace />}>
                        <Customers />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/staff"
                    element={
                      <RequirePermission permission="staff:read" fallback={<Navigate to="/" replace />}>
                        <Staff />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/tasas"
                    element={
                      <RequirePermission permission="rates:update" fallback={<Navigate to="/" replace />}>
                        <ExchangeRates />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/catalogo"
                    element={
                      <RequirePermission permission="settings:manage" fallback={<Navigate to="/" replace />}>
                        <Catalog />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <RequirePermission permission="settings:manage" fallback={<Navigate to="/" replace />}>
                        <Settings />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/usuarios"
                    element={
                      <RequirePermission permission="users:manage" fallback={<Navigate to="/" replace />}>
                        <Users />
                      </RequirePermission>
                    }
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AppShell>
            </ProtectedRoute>
          }
        />
      </Routes>
      <UpdatePrompt />
    </Suspense>
  );
}
